// packages/web/src/components/ClassroomLive.tsx — misma lógica (polling, regenerar, cerrar, insight, analytics).
// Cambios: encabezado con estado en vivo, cifras resumen, confirmación en diálogo propio,
// estados en palabras, avance por nivel Marzano, acciones por fila.
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2, Sparkles, ThumbsUp, Target, Lightbulb, X, Link2 } from 'lucide-react';
import {
  CLASSROOM_LIVE_POLL_MS,
  CLASSROOM_LIVE_POLL_STOP_AFTER_ERRORS,
  shouldTriggerRemedial,
  type DictamenState,
} from '@marzano/contracts';
import {
  api,
  ApiError,
  type AttentionFlag,
  type ClassroomAnalyticsResponse,
  type ClassroomLive as ClassroomLiveType,
  type ClassroomLivePilot,
} from '../api';
import { ALL_DIMS, DIM_LABEL, DICTAMEN_LABEL, type Dim } from '../types';
import { Alert, Button, Card, Copyable, PageHeader, Stat, StatusBadge, TABLE, shortDate } from './ui';

interface Props {
  classroomId: string;
  onBack: () => void;
}

const TERMINAL = ['closed_evidence', 'closed_time', 'closed_early_high_confidence', 'abandoned', 'pending_teacher_review', 'reviewed'];
const isTerminalState = (s: string | null) => s !== null && TERMINAL.includes(s);
const DIM_COLOR: Record<Dim, string> = { recuperacion: 'bg-[#2B5C9E]', comprension: 'bg-[#0B7A72]', analisis: 'bg-[#6A47A6]', transferencia: 'bg-[#A0530B]' };

export function ClassroomLive({ classroomId, onBack }: Props) {
  const [data, setData] = useState<ClassroomLiveType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [regeneratedLink, setRegeneratedLink] = useState<{ pilotId: string; link: string } | null>(null);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [insightPilot, setInsightPilot] = useState<ClassroomLivePilot | null>(null);
  const errorStreakRef = useRef(0);
  const pollStoppedRef = useRef(false);

  const fetchLive = useCallback(async () => {
    try {
      const live = await api.getClassroomLive(classroomId);
      setData(live);
      setError(null);
      errorStreakRef.current = 0;
    } catch (err) {
      errorStreakRef.current += 1;
      setError(err instanceof ApiError ? err.message : 'No se pudo cargar el aula');
    } finally {
      setLoading(false);
    }
  }, [classroomId]);

  useEffect(() => {
    void fetchLive();
    errorStreakRef.current = 0;
    pollStoppedRef.current = false;
    const t = window.setInterval(() => {
      if (errorStreakRef.current >= CLASSROOM_LIVE_POLL_STOP_AFTER_ERRORS) {
        pollStoppedRef.current = true;
        window.clearInterval(t);
        return;
      }
      void fetchLive();
    }, CLASSROOM_LIVE_POLL_MS);
    return () => window.clearInterval(t);
  }, [fetchLive]);

  async function handleRegenerate(pilotId: string): Promise<void> {
    setRegenerating(pilotId);
    try {
      const resp = await api.regenerateClassroomInvite(classroomId, pilotId);
      setRegeneratedLink({ pilotId, link: resp.invite_link });
      await fetchLive();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'No se pudo regenerar el link');
    } finally {
      setRegenerating(null);
    }
  }

  // Misma acción que antes; el window.confirm se reemplaza por un diálogo accesible.
  async function handleClose(): Promise<void> {
    if (!data) return;
    setConfirmClose(false);
    setClosing(true);
    try {
      await api.closeClassroom(classroomId);
      await fetchLive();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'No se pudo cerrar el aula');
    } finally {
      setClosing(false);
    }
  }

  if (loading && !data) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 aria-label="Cargando" className="w-6 h-6 animate-spin text-slate-500" /></div>;
  }
  if (error && !data) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <PageHeader back={{ label: 'Mis aulas', onClick: onBack }} title="No se pudo abrir el aula" />
        <Alert kind="error">{error}</Alert>
      </div>
    );
  }
  if (!data) return null;
  const { header, pilots } = data;

  const counts = {
    notStarted: pilots.filter((p) => p.current_state === null || p.current_state === 'created').length,
    inProgress: pilots.filter((p) => !isTerminalState(p.current_state) && p.current_state !== null && p.current_state !== 'created').length,
    done: pilots.filter((p) => isTerminalState(p.current_state)).length,
    attention: pilots.filter((p) => p.attention_flags.length > 0).length,
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader
        back={{ label: 'Mis aulas', onClick: onBack }}
        title={header.name}
        subtitle={<>{header.level_name} · {header.oa_code}</>}
        actions={
          <>
            {header.closed_at ? (
              <StatusBadge tone="muted">Aula cerrada</StatusBadge>
            ) : pollStoppedRef.current ? (
              <StatusBadge tone="danger">Actualización detenida · recarga la página</StatusBadge>
            ) : (
              <span className="inline-flex items-center gap-2 text-[13px] text-slate-700" aria-live="off">
                <span className="relative flex h-2 w-2" aria-hidden>
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
                </span>
                <strong className="font-semibold text-emerald-800">En vivo</strong> · cada {CLASSROOM_LIVE_POLL_MS / 1000} s
              </span>
            )}
            {!header.closed_at && (
              <Button variant="danger" size="sm" onClick={() => setConfirmClose(true)} loading={closing}>Cerrar aula</Button>
            )}
          </>
        }
      />

      {error && <Alert kind="warn">La última actualización falló: {error}</Alert>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Sin empezar" value={counts.notStarted} />
        <Stat label="Respondiendo" value={counts.inProgress} />
        <Stat label="Terminaron" value={counts.done} hint={`de ${pilots.length} alumnos`} />
        <Stat label="Requieren tu atención" value={counts.attention} tone={counts.attention > 0 ? 'warn' : 'default'} />
      </div>

      {regeneratedLink && (
        <Alert kind="success">
          <div className="space-y-2 min-w-0"><p className="font-semibold">Nuevo link generado. Cópialo y envíalo al alumno:</p><Copyable value={regeneratedLink.link} /></div>
        </Alert>
      )}

      <AnalyticsCard classroomId={classroomId} pilots={pilots} />
      <AttentionBanner pilots={pilots} onPilotClick={(p) => setInsightPilot(p)} />
      {insightPilot && <InsightModal classroomId={classroomId} pilot={insightPilot} onClose={() => setInsightPilot(null)} />}

      <div className={TABLE.wrap}>
        <table className={TABLE.table}>
          <caption className="sr-only">Alumnos del aula; primero los que requieren atención</caption>
          <thead className={TABLE.thead}>
            <tr>
              <th scope="col" className={TABLE.th}>Alumno</th>
              <th scope="col" className={TABLE.th}>Estado</th>
              <th scope="col" className={TABLE.th}>Avance</th>
              <th scope="col" className={TABLE.th}>Puntaje</th>
              <th scope="col" className={TABLE.th}>Última actividad</th>
              <th scope="col" className={TABLE.th}><span className="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody>
            {sortByAttention(pilots).map((p) => (
              <PilotRow key={p.classroom_pilot_id} pilot={p}
                onRegenerate={() => void handleRegenerate(p.pilot_id)}
                onOpenInsight={() => setInsightPilot(p)}
                regenerating={regenerating === p.pilot_id}
                classroomClosed={header.closed_at !== null} />
            ))}
          </tbody>
        </table>
      </div>

      {confirmClose && (
        <Dialog title="¿Cerrar el aula?" onClose={() => setConfirmClose(false)}>
          <p className="text-sm text-slate-700 leading-relaxed">Los alumnos que aún no empiezan ya no podrán entrar. Los que están respondiendo terminan normalmente.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setConfirmClose(false)}>Cancelar</Button>
            <Button variant="danger" className="!bg-red-700 !text-white !border-red-700 hover:!bg-red-800" onClick={() => void handleClose()}>Cerrar aula</Button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function sortByAttention(pilots: readonly ClassroomLivePilot[]): ClassroomLivePilot[] {
  const rank = (p: ClassroomLivePilot): number => {
    if (p.attention_flags.length > 0) return 0;
    if (p.current_state === 'in_progress' || p.current_state === 'warming_up') return 1;
    return 2;
  };
  return [...pilots].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.learner_email.localeCompare(b.learner_email);
  });
}

function AttentionBanner({ pilots, onPilotClick }: { pilots: readonly ClassroomLivePilot[]; onPilotClick: (p: ClassroomLivePilot) => void }) {
  const needing = pilots.filter((p) => p.attention_flags.length > 0);
  if (needing.length === 0) return null;
  return (
    <section aria-labelledby="att-t" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
      <h2 id="att-t" className="text-sm font-semibold text-amber-900 mb-2.5">
        ⚠ Necesitan atención ({needing.length}) <span className="font-normal text-amber-900/80">· abre uno para ver el análisis IA</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {needing.slice(0, 6).map((p) => (
          <button type="button" key={p.classroom_pilot_id} onClick={() => onPilotClick(p)}
            className="text-left rounded-lg border border-amber-200 bg-white px-3 py-2.5 hover:border-amber-400 hover:shadow-sm transition">
            <div className="font-semibold text-sm text-slate-900 truncate">{p.learner_name_snapshot ?? p.learner_email}</div>
            <div className="mt-1.5 flex flex-wrap gap-1">{p.attention_flags.map((f, i) => <AttentionChip key={i} flag={f} />)}</div>
          </button>
        ))}
      </div>
      {needing.length > 6 && <p className="text-[13px] text-amber-900 mt-2">y {needing.length - 6} más en la tabla.</p>}
    </section>
  );
}

function AttentionChip({ flag }: { flag: AttentionFlag }) {
  const { text, style } = describeFlag(flag);
  return <span className={`inline-block rounded-md px-1.5 py-0.5 text-[11px] font-semibold border ${style}`}>{text}</span>;
}

function humanDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  return `${Math.floor(hours / 24)} d`;
}

function describeFlag(flag: AttentionFlag): { text: string; style: string } {
  switch (flag.kind) {
    case 'inactive': return { text: `Sin responder hace ${humanDuration(flag.minutes)}`, style: 'bg-amber-100 text-amber-900 border-amber-200' };
    case 'many_helps': return { text: `${flag.count} ayudas seguidas`, style: 'bg-orange-100 text-orange-900 border-orange-200' };
    case 'block_detected': return { text: 'Bloqueo detectado', style: 'bg-red-100 text-red-800 border-red-200' };
    case 'ai_suspicion': return { text: flag.level === 'high' ? 'Posible uso de IA (alto)' : 'Posible uso de IA', style: 'bg-violet-100 text-violet-800 border-violet-200' };
    case 'no_evidence_yet': return { text: `Sin evidencias hace ${humanDuration(flag.minutes)}`, style: 'bg-slate-100 text-slate-800 border-slate-200' };
  }
}

function PilotRow({ pilot, onRegenerate, onOpenInsight, regenerating, classroomClosed }: {
  pilot: ClassroomLivePilot; onRegenerate: () => void; onOpenInsight: () => void; regenerating: boolean; classroomClosed: boolean;
}) {
  const inviteExpired = new Date(pilot.invite_expires_at).getTime() < Date.now();
  const isTerminal = isTerminalState(pilot.current_state);
  const chip = getStatusChip(pilot.current_state, isTerminal);
  const hasFlags = pilot.attention_flags.length > 0;
  const dim = pilot.current_dim as Dim | null;
  const dimIdx = dim ? ALL_DIMS.indexOf(dim) : -1;
  const name = pilot.learner_name_snapshot ?? pilot.learner_email;

  return (
    <tr className={TABLE.tr + (hasFlags ? ' bg-amber-50/50' : '')}>
      <td className={TABLE.td}>
        <div className="font-semibold text-slate-900">{name}</div>
        {pilot.learner_name_snapshot && <div className="text-[13px] text-slate-600">{pilot.learner_email}</div>}
        {hasFlags && <div className="mt-1.5 flex flex-wrap gap-1">{pilot.attention_flags.map((f, i) => <AttentionChip key={i} flag={f} />)}</div>}
      </td>
      <td className={TABLE.td}><StatusBadge tone={chip.tone}>{chip.label}</StatusBadge></td>
      <td className={TABLE.td + ' min-w-[160px]'}>
        {pilot.turns_answered > 0 ? (
          <div className="flex flex-col gap-1.5">
            <div className="grid grid-cols-4 gap-0.5 w-32" aria-hidden>
              {ALL_DIMS.map((d, i) => <span key={d} className={`h-1.5 rounded-full ${i <= dimIdx ? DIM_COLOR[d] : 'bg-slate-200'}`} />)}
            </div>
            <span className="text-[13px] text-slate-700">
              {dim ? `${dimIdx + 1} · ${DIM_LABEL[dim]}` : '—'} · {pilot.turns_answered} turno{pilot.turns_answered !== 1 ? 's' : ''}
            </span>
          </div>
        ) : <span className="text-slate-500">—</span>}
      </td>
      <td className={TABLE.td}>
        {pilot.global_score !== null ? (
          <div className="flex flex-col gap-1 items-start">
            <span>
              <strong className="text-base tabular-nums">{pilot.global_score.toFixed(1)}</strong>
              {pilot.dictamen_state && <span className="text-[13px] text-slate-600 ml-1.5">{DICTAMEN_LABEL[pilot.dictamen_state as DictamenState] ?? pilot.dictamen_state}</span>}
            </span>
            {pilot.remedial && <RemedialBadge remedial={pilot.remedial} />}
            {!pilot.remedial && pilot.dictamen_state && shouldTriggerRemedial(pilot.dictamen_state as DictamenState) && (
              <span title="Se genera cuando el alumno abre su resultado."><StatusBadge tone="warn">Guía remedial pendiente</StatusBadge></span>
            )}
          </div>
        ) : <span className="text-slate-500">—</span>}
      </td>
      <td className={TABLE.td + ' text-slate-700 whitespace-nowrap'}>
        {pilot.last_activity_at ? <time dateTime={pilot.last_activity_at} title={new Date(pilot.last_activity_at).toLocaleString('es-CL')}>{shortDate(pilot.last_activity_at)}</time> : '—'}
      </td>
      <td className={TABLE.td + ' text-right whitespace-nowrap'}>
        <div className="flex justify-end gap-2">
          {hasFlags && <Button variant="secondary" size="sm" onClick={onOpenInsight} leftIcon={<Sparkles aria-hidden className="w-4 h-4" />}>Análisis</Button>}
          {!isTerminal && !classroomClosed && inviteExpired && (
            <Button variant="secondary" size="sm" onClick={onRegenerate} loading={regenerating} leftIcon={<Link2 aria-hidden className="w-4 h-4" />}>Nuevo link</Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function RemedialBadge({ remedial }: { remedial: NonNullable<ClassroomLivePilot['remedial']> }) {
  const round = remedial.rounds_total > 1 ? ` · R${remedial.latest_round}` : '';
  if (remedial.retry_session_id) return <span title="Trabajo aprobado por la IA; reintento habilitado."><StatusBadge tone="info">Reintento habilitado{round}</StatusBadge></span>;
  if (remedial.validation_status === 'rejected') return <span title="La IA rechazó el trabajo. El alumno debe corregir y volver a subir."><StatusBadge tone="danger">Trabajo rechazado{round}</StatusBadge></span>;
  if (remedial.upload_ready) return <span title="El alumno subió el trabajo; la IA está revisando."><StatusBadge tone="muted">IA revisando trabajo{round}</StatusBadge></span>;
  if (remedial.guide_ready) return <span title="Guía generada; esperando el trabajo del alumno."><StatusBadge tone="warn">Esperando trabajo{round}</StatusBadge></span>;
  return <span title="La guía se genera cuando el alumno abre su resultado."><StatusBadge tone="warn">Preparando guía{round}</StatusBadge></span>;
}

function getStatusChip(state: string | null, isTerminal: boolean): { label: string; tone: 'ok' | 'info' | 'muted' } {
  if (isTerminal) return { label: 'Terminada', tone: 'ok' };
  if (state === 'in_progress' || state === 'warming_up') return { label: 'Respondiendo', tone: 'info' };
  if (state === null || state === 'created') return { label: 'Sin empezar', tone: 'muted' };
  return { label: state, tone: 'muted' };
}

function Dialog({ title, onClose, children, wide }: { title: React.ReactNode; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
        className={`bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full ${wide ? 'max-w-lg' : 'max-w-md'} max-h-[85vh] overflow-y-auto p-5 space-y-3`}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="p-1 rounded-md text-slate-500 hover:text-slate-800"><X aria-hidden className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function InsightModal({ classroomId, pilot, onClose }: { classroomId: string; pilot: ClassroomLivePilot; onClose: () => void }) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setText(null);
    void api.getAttentionInsight(classroomId, pilot.pilot_id)
      .then((r) => { if (cancelled) return; setText(r.text); setFromCache(r.from_cache); })
      .catch((err) => { if (cancelled) return; setError(err instanceof ApiError ? err.message : 'No pudimos generar el análisis. Reintenta en un momento.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [classroomId, pilot.pilot_id]);

  const dim = pilot.current_dim as Dim | null;
  return (
    <Dialog wide onClose={onClose} title={
      <span className="flex flex-col"><span className="text-[13px] font-normal text-marzano-600 flex items-center gap-1"><Sparkles aria-hidden className="w-3.5 h-3.5" />Análisis con IA</span>{pilot.learner_name_snapshot ?? pilot.learner_email}</span>
    }>
      <p className="text-[13px] text-slate-600">{pilot.turns_answered} turno{pilot.turns_answered === 1 ? '' : 's'} respondidos{dim && ` · nivel actual: ${DIM_LABEL[dim]}`}</p>
      <div className="flex flex-wrap gap-1.5">{pilot.attention_flags.map((f, i) => <AttentionChip key={i} flag={f} />)}</div>
      {loading && <p className="flex items-center gap-2 text-sm text-slate-700 py-4"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Analizando los últimos turnos…</p>}
      {error && !loading && <Alert kind="error">{error}</Alert>}
      {text && !loading && (
        <div className="rounded-lg border border-marzano-100 bg-marzano-50 px-3.5 py-3">
          <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{text}</p>
          {fromCache && <p className="text-xs text-slate-600 mt-2">Análisis guardado hace menos de 5 min. Se actualiza cuando el alumno responda.</p>}
        </div>
      )}
      <p className="text-xs text-slate-600">Es una hipótesis de la IA basada en los últimos turnos. Úsala como referencia, no como diagnóstico.</p>
    </Dialog>
  );
}

function AnalyticsCard({ classroomId, pilots }: { classroomId: string; pilots: readonly ClassroomLivePilot[] }) {
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<ClassroomAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const closedCount = pilots.filter((p) => p.dictamen_state !== null).length;
  const MIN = 3;

  const load = useCallback(async (force: boolean) => {
    if (force) setRegenerating(true); else setLoading(true);
    setError(null);
    try {
      const r = force ? await api.regenerateClassroomAnalytics(classroomId) : await api.getClassroomAnalytics(classroomId);
      setData(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar el análisis del aula. Reintenta en un momento.');
    } finally {
      setLoading(false); setRegenerating(false);
    }
  }, [classroomId]);

  useEffect(() => {
    if (expanded && data === null && !loading && error === null) void load(false);
  }, [expanded, data, loading, error, load]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <button type="button" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 rounded-xl">
        <span className="rounded-full bg-marzano-50 p-2 shrink-0"><Sparkles aria-hidden className="w-4 h-4 text-marzano-600" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-slate-900">Análisis del aula</span>
          <span className="block text-[13px] text-slate-600">
            {closedCount >= MIN ? `Basado en ${closedCount} reportes cerrados` : `Se activa con ${MIN} reportes cerrados · llevas ${closedCount}`}
          </span>
        </span>
        {closedCount < MIN && (
          <span aria-hidden className="hidden sm:block w-28 h-1.5 rounded-full bg-slate-100">
            <span className="block h-1.5 rounded-full bg-marzano-600" style={{ width: `${(closedCount / MIN) * 100}%` }} />
          </span>
        )}
        <ChevronDown aria-hidden className={`w-4 h-4 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-4">
          {loading && <p className="flex items-center gap-2 text-sm text-slate-700 py-4"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />La IA está analizando los reportes del aula…</p>}
          {error && !loading && <div className="space-y-2"><Alert kind="error">{error}</Alert><Button variant="secondary" size="sm" onClick={() => void load(false)}>Reintentar</Button></div>}
          {data?.status === 'not_enough_data' && !loading && (
            <Alert kind="info">Se necesitan al menos <strong>{data.min_required}</strong> reportes cerrados. Hoy hay <strong>{data.dictamen_count_at_check}</strong>.</Alert>
          )}
          {data?.status === 'ready' && !loading && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <AnalyticsColumn kind="strengths" title="Fortalezas" items={data.content.strengths} />
                <AnalyticsColumn kind="areas" title="Para mejorar" items={data.content.areas} />
                <AnalyticsColumn kind="followup" title="Próximos pasos" items={data.content.followup} />
              </div>
              {data.content.notes && data.content.notes.length > 0 && <Alert kind="info"><strong>Nota:</strong> {data.content.notes}</Alert>}
              <div className="flex items-center justify-between gap-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-600">
                  {data.last_dictamen_count} reportes · {data.from_cache ? 'guardado (30 min)' : 'recién generado'} · {new Date(data.generated_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                </p>
                <Button variant="secondary" size="sm" onClick={() => void load(true)} loading={regenerating}>Regenerar</Button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function AnalyticsColumn({ kind, title, items }: { kind: 'strengths' | 'areas' | 'followup'; title: string; items: readonly string[] }) {
  const s = {
    strengths: { head: 'text-emerald-800', icon: <ThumbsUp aria-hidden className="w-4 h-4" /> },
    areas: { head: 'text-amber-900', icon: <Target aria-hidden className="w-4 h-4" /> },
    followup: { head: 'text-marzano-700', icon: <Lightbulb aria-hidden className="w-4 h-4" /> },
  }[kind];
  return (
    <Card className="!p-4 !space-y-2 bg-slate-50/60">
      <h3 className={`flex items-center gap-1.5 text-sm font-semibold ${s.head}`}>{s.icon}{title}</h3>
      {items.length === 0 ? <p className="text-[13px] text-slate-600">Sin observaciones puntuales.</p> : (
        <ul className="space-y-1.5 text-sm text-slate-800 list-disc pl-4 marker:text-slate-400">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
      )}
    </Card>
  );
}
